/*
 * Blue Ridge Laces — PDP BuyBox behavior
 *
 * - Swatches / pills / aglet tiles act as a radio group per option.
 * - On change, we find the variant whose options match, then update:
 *   - the hidden `id` input (so <form action="/cart/add"> submits correctly)
 *   - the visible price, compare-at price, CTA label, CTA state
 *   - the aglet option tile labels (if section provided a JSON map)
 *   - the length hint text under the length pills
 * - Gallery thumbnails swap the main image source.
 *
 * Scoped to elements inside `.brl-pdp-section` — one instance per section.
 */

(function () {
  const formatMoney = (cents) => {
    // Minimal formatter; relies on the shop's currency being USD.
    // For multi-currency stores, swap in Shopify.formatMoney if loaded.
    const dollars = (cents / 100).toFixed(2).replace(/\.00$/, "");
    return `$${dollars}`;
  };

  class BrlPdpBuybox {
    constructor(root) {
      this.root = root;
      const variantsTag = root.querySelector("[data-brl-variants]");
      if (!variantsTag) return;
      try {
        this.variants = JSON.parse(variantsTag.textContent);
      } catch (e) {
        console.error("BRL: bad variant JSON", e);
        this.variants = [];
      }

      this.form = root.querySelector("[data-brl-product-form]");
      this.variantInput = root.querySelector("[data-brl-variant-id]");
      this.priceEl = root.querySelector("[data-brl-price]");
      this.compareEl = root.querySelector("[data-brl-compare-price]");
      this.ctaLabel = root.querySelector("[data-brl-cta-label]");
      this.ctaPrice = root.querySelector("[data-brl-cta-price]");
      this.ctaBtn = root.querySelector("[data-brl-add-to-cart]");
      this.qtyInput = root.querySelector("[data-brl-qty-input]");
      this.optionGroups = Array.from(root.querySelectorAll("[data-brl-option-group]"))
        .sort((a, b) => {
          const ap = parseInt(a.getAttribute("data-option-position"), 10) || 0;
          const bp = parseInt(b.getAttribute("data-option-position"), 10) || 0;
          return ap - bp;
        });

      this.agletPrices = {};
      const agletTag = root.querySelector("[data-brl-aglet-prices]");
      if (agletTag) {
        try {
          this.agletPrices = JSON.parse(agletTag.textContent);
        } catch (e) { /* ignore */ }
      }
      this.applyAgletPrices();

      this.bindOptionInputs();
      this.bindAgletToggle();
      this.bindQty();
      this.bindGallery();

      // Kick off once so the UI is in sync with the pre-selected variant.
      this.syncFromSelection();
      this.preselectFromUrl();
    }

    preselectFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const length = params.get("length");
      if (!length) return;
      const target = length + '"';
      for (const group of this.optionGroups) {
        const name = (group.getAttribute("data-option-name") || "").toLowerCase();
        if (name !== "length") continue;
        const input = group.querySelector('input[value="' + CSS.escape(target) + '"]');
        if (input && !input.checked) {
          input.checked = true;
          this.updateActiveStyling(input);
          this.syncFromSelection();
        }
        break;
      }
    }

    bindOptionInputs() {
      this.root.querySelectorAll("[data-brl-option-input]").forEach((input) => {
        input.addEventListener("change", () => {
          this.updateActiveStyling(input);
          this.syncFromSelection();
        });
      });

      // Clicks on labels bubble to the input change normally, but we also
      // listen to label clicks so the visual state can update even if the
      // input is already checked (edge cases with programmatic state).
      this.root.querySelectorAll("[data-brl-option-value]").forEach((label) => {
        label.addEventListener("click", () => {
          const input = label.querySelector("[data-brl-option-input]");
          if (input && !input.checked) {
            input.checked = true;
            this.updateActiveStyling(input);
            this.syncFromSelection();
          }
        });
      });
    }

    updateActiveStyling(changedInput) {
      const group = changedInput.closest("[data-brl-option-group]");
      if (!group) return;
      group.querySelectorAll("[data-brl-option-value]").forEach((label) => {
        const input = label.querySelector("[data-brl-option-input]");
        label.classList.toggle("is-active", !!input && input.checked);
      });
      const valueDisplay = group.querySelector("[data-brl-option-value]");
      if (valueDisplay && group.querySelector("[data-brl-option-value] + [data-brl-option-value]") === null) {
        // no-op; the header value is updated via syncFromSelection below
      }
    }

    currentSelection() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector("[data-brl-option-input]:checked");
        return checked ? checked.value : null;
      });
    }

    findVariant(selection) {
      return this.variants.find((v) => {
        return (
          (v.option1 || null) === (selection[0] || null) &&
          (v.option2 || null) === (selection[1] || null) &&
          (v.option3 || null) === (selection[2] || null)
        );
      });
    }

    syncFromSelection() {
      const selection = this.currentSelection();
      const variant = this.findVariant(selection);

      this.optionGroups.forEach((group, i) => {
        const headerValue = group.querySelector(".brl-field__header [data-brl-option-value]");
        if (headerValue && !headerValue.querySelector("input")) {
          headerValue.textContent = selection[i] ? "— " + selection[i] : "";
        }
      });

      if (!variant) {
        this.ctaBtn.disabled = true;
        if (this.ctaLabel) this.ctaLabel.textContent = "Unavailable";
        if (this.ctaPrice) this.ctaPrice.textContent = "";
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;

      this.filterGalleryByColor(selection);

      if (this.priceEl) this.priceEl.textContent = formatMoney(variant.price);
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.textContent = formatMoney(variant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.textContent = "";
          this.compareEl.hidden = true;
        }
      }

      const qty = parseInt(this.qtyInput ? this.qtyInput.value : "1", 10) || 1;
      const total = variant.price * qty;
      if (this.ctaPrice) this.ctaPrice.textContent = formatMoney(total);

      if (variant.available) {
        this.ctaBtn.disabled = false;
        if (this.ctaLabel) this.ctaLabel.textContent = "Add to Cart";
      } else {
        this.ctaBtn.disabled = true;
        if (this.ctaLabel) this.ctaLabel.textContent = "Sold Out";
      }

      // Update URL with variant id so a refresh preserves selection
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState({}, "", url.toString());
      }
    }

    applyAgletPrices() {
      if (!this.agletPrices || Object.keys(this.agletPrices).length === 0) return;
      this.root.querySelectorAll("[data-brl-aglet-price]").forEach((el) => {
        const value = el.getAttribute("data-value");
        if (value && this.agletPrices[value] != null) {
          el.textContent = this.agletPrices[value];
        }
      });
    }

    bindAgletToggle() {
      const checkbox = this.root.querySelector("[data-brl-aglet-toggle]");
      if (!checkbox) return;
      const optionName = checkbox.getAttribute("data-option-name");
      const upgradeValue = checkbox.getAttribute("data-upgrade-value");
      const defaultValue = checkbox.getAttribute("data-default-value");
      const group = this.optionGroups.find(
        (g) => g.getAttribute("data-option-name") === optionName
      );
      if (!group) return;

      checkbox.addEventListener("change", () => {
        const target = checkbox.checked ? upgradeValue : defaultValue;
        const radio = group.querySelector(
          'input[value="' + CSS.escape(target) + '"]'
        );
        if (radio) {
          radio.checked = true;
          this.syncFromSelection();
        }
      });
    }

    bindQty() {
      if (!this.qtyInput) return;
      const dec = this.root.querySelector("[data-brl-qty-decrement]");
      const inc = this.root.querySelector("[data-brl-qty-increment]");
      if (dec) {
        dec.addEventListener("click", () => {
          const v = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) - 1);
          this.qtyInput.value = v;
          this.syncFromSelection();
        });
      }
      if (inc) {
        inc.addEventListener("click", () => {
          const v = (parseInt(this.qtyInput.value, 10) || 1) + 1;
          this.qtyInput.value = v;
          this.syncFromSelection();
        });
      }
      this.qtyInput.addEventListener("input", () => this.syncFromSelection());
    }

    bindGallery() {
      const main = this.root.querySelector("[data-brl-gallery-main]");
      if (!main) return;
      const thumbs = this.root.querySelectorAll("[data-brl-gallery-thumb]");
      const dots = this.root.querySelectorAll("[data-brl-gallery-dot]");

      const showMedia = (index) => {
        const source = thumbs[index] || dots[index];
        if (!source) return;
        const src = source.getAttribute("data-media-src");
        const alt = source.getAttribute("data-media-alt") || "";
        const mediaId = source.getAttribute("data-media-id") || "";
        if (src) {
          const img = main.querySelector("img");
          if (img) {
            img.src = src;
            img.srcset = "";
            if (alt) img.alt = alt;
          }
        }
        if (mediaId) main.setAttribute("data-media-id", mediaId);
        thumbs.forEach((t, i) => t.classList.toggle("is-active", i === index));
        dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
      };

      thumbs.forEach((thumb, i) => {
        thumb.addEventListener("click", () => showMedia(i));
      });
      dots.forEach((dot, i) => {
        dot.addEventListener("click", () => showMedia(i));
      });
    }

    filterGalleryByColor(selection) {
      const gallery = this.root.querySelector("[data-brl-gallery]");
      if (!gallery) return;
      const main = this.root.querySelector("[data-brl-gallery-main]");
      if (!main) return;

      const colorIndex = this.colorOptionIndex();
      if (colorIndex === -1) return;
      const color = (selection[colorIndex] || "").toLowerCase();
      if (!color) return;

      const prevColor = (gallery.getAttribute("data-selected-color") || "").toLowerCase();
      if (color === prevColor) return;
      gallery.setAttribute("data-selected-color", selection[colorIndex]);

      const thumbs = Array.from(this.root.querySelectorAll("[data-brl-gallery-thumb]"));
      const dots = Array.from(this.root.querySelectorAll("[data-brl-gallery-dot]"));

      const colorTag = "color: " + color;
      const matchesColor = (el) => {
        const alt = (el.getAttribute("data-media-alt") || "").toLowerCase();
        if (!alt.includes("color:")) return true; // no color tag = shared image
        return alt.includes(colorTag);
      };

      const hasMatches =
        thumbs.some(matchesColor) || dots.some(matchesColor);

      thumbs.forEach((t) => {
        t.hidden = hasMatches ? !matchesColor(t) : false;
      });
      dots.forEach((d) => {
        d.hidden = hasMatches ? !matchesColor(d) : false;
      });

      const firstVisible =
        thumbs.find((t) => !t.hidden) || dots.find((d) => !d.hidden);
      if (firstVisible) {
        const src = firstVisible.getAttribute("data-media-src");
        const alt = firstVisible.getAttribute("data-media-alt") || "";
        const mediaId = firstVisible.getAttribute("data-media-id") || "";
        const img = main.querySelector("img");
        if (img && src) {
          img.src = src;
          img.srcset = "";
          if (alt) img.alt = alt;
        }
        if (mediaId) main.setAttribute("data-media-id", mediaId);
        thumbs.forEach((t) =>
          t.classList.toggle("is-active", t.getAttribute("data-media-id") === mediaId)
        );
        dots.forEach((d) =>
          d.classList.toggle("is-active", d.getAttribute("data-media-id") === mediaId)
        );
      }
    }

    colorOptionIndex() {
      for (let i = 0; i < this.optionGroups.length; i++) {
        const name = (this.optionGroups[i].getAttribute("data-option-name") || "").toLowerCase();
        if (name === "color" || name === "colour") return i;
      }
      return -1;
    }
  }

  function init() {
    document
      .querySelectorAll(".brl-pdp-section")
      .forEach((el) => {
        if (!el.dataset.brlInit) {
          new BrlPdpBuybox(el);
          el.dataset.brlInit = "1";
        }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init after Shopify theme editor section reloads
  document.addEventListener("shopify:section:load", init);
})();
